// Threshold-based trophy unlock predicates. Trophy names match the existing
// TROPHIES list in realms.ts. Trophies whose criteria can't be derived from
// available data (e.g. social, hidden) are absent here and stay locked.

import type { XpEvent } from '../lib/progress';

export interface TrophyInputs {
  solvedCount: number;
  streak: number;
  solvedByTier: { mid: number; cracked: number; goated: number };
  solvedByTopic: Record<string, number>;
  solvedSet: Set<string>;
  xpEvents: XpEvent[];
  realmsVisited: number; // realms with >= 1 solve
  friendCount: number;
}

type Predicate = (u: TrophyInputs) => boolean;

const hourOfDay = (ts: number) => new Date(ts).getHours();
const hasSolveInHourRange = (u: TrophyInputs, lo: number, hi: number) =>
  u.xpEvents.some(e => {
    const h = hourOfDay(e.ts);
    return h >= lo && h < hi;
  });

// Maps trophy `name` → predicate. Auto-unlocks evaluated at render time.
export const TROPHY_PREDICATES: Record<string, Predicate> = {
  // ── Streak family ──
  'first spark':       u => u.streak >= 1,
  'week warrior':      u => u.streak >= 7,
  'fortnight phenom':  u => u.streak >= 14,
  'monthly menace':    u => u.streak >= 30,
  'centurion':         u => u.streak >= 100,
  'year of code':      u => u.streak >= 365,

  // ── Count family ──
  'first solve':       u => u.solvedCount >= 1,
  'first 10':          u => u.solvedCount >= 10,
  'half-century':      u => u.solvedCount >= 50,
  'centurion solver':  u => u.solvedCount >= 100,
  'half a thousand':   u => u.solvedCount >= 500,
  'thousand-club':     u => u.solvedCount >= 1000,

  // ── Realm family — "visit" = solve at least 1 problem in that realm ──
  'island hopper':     u => u.realmsVisited >= 3,
  'cartographer':      u => u.realmsVisited >= 8,
  // realm-finished trophies require knowing realm-completion %, computed at
  // ProfilePage render where REALMS catalog is in scope; left out here.

  // ── Speed family ──
  'one-shot':          u => u.solvedCount >= 1,        // any solve counts in Phase A (no attempt data)

  // ── Time family ──
  'night owl':         u => hasSolveInHourRange(u, 0, 5),
  'early bird':        u => hasSolveInHourRange(u, 4, 6),
  'lunch break':       u => hasSolveInHourRange(u, 11, 14),

  // ── Rare family ──
  'fizzbuzz friend':   () => true,                      // visiting the app = met the mascot
  'jack of all':       u => u.realmsVisited >= 8,

  // ── Social family ──
  'social butterfly':  u => u.friendCount >= 5,
};

export function evaluateTrophy(name: string, inputs: TrophyInputs): boolean {
  const pred = TROPHY_PREDICATES[name];
  return pred ? pred(inputs) : false;
}
